import { ReactNode, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger } from "../shadcn/Tabs";
import VerticalDivider from "../common/VerticalDivider";
import PageTitle from "../common/PageTitle";
import SearchSummary from "./SearchSummary";
import { SearchResultsFormatType } from "./useSearchListingsPage";
import { ActionsMenu, MenuItem } from "../neighborhoodSearch/savedSearch/ActionsMenu";
import InkedButton from "../sendCards/InkedButton";

enum EnumPageTabs {
  Map = "map",
  List = "list",
}

interface SearchPageTitleProps {
  title: string | ReactNode;
  titleIcon?: ReactNode;
  isPro?: boolean;
  totalRecords?: number;
  isLoading?: boolean;
  currentPage?: number;
  pageSize?: number;
  onToggleClicked?: () => void;
  superscriptText?: string;
  centerContent?: ReactNode;
  subtitle?: string;
  isCondensed?: boolean;
  isUseGutters?: boolean;
  searchResultsFormat: typeof SearchResultsFormatType[keyof typeof SearchResultsFormatType];
  setSearchResultsFormat: (format: typeof SearchResultsFormatType[keyof typeof SearchResultsFormatType]) => void;
  buttonsContent?: ReactNode;
  actionsMenuItems?: MenuItem[];
  onSendCardClick?: () => void;
  sendCardCount?: number;
  isBatchSelectionActive?: boolean;
}

const SearchPageTitle = ({
  searchResultsFormat,
  setSearchResultsFormat,
  buttonsContent,
  title,
  titleIcon,
  isPro = false,
  totalRecords,
  isLoading = true,
  currentPage,
  pageSize,
  onToggleClicked,
  superscriptText,
  centerContent,
  subtitle,
  isCondensed,
  isUseGutters,
  actionsMenuItems = [],
  onSendCardClick,
  sendCardCount = 0,
  isBatchSelectionActive = false,
}: SearchPageTitleProps) => {
  // Convert SearchResultsFormatType to tab format
  const activePageTab = searchResultsFormat === SearchResultsFormatType.map ? EnumPageTabs.Map : EnumPageTabs.List;
  
  const handlePageTabChange = useCallback((tab: string) => {
    const newFormat = tab === EnumPageTabs.Map ? SearchResultsFormatType.map : SearchResultsFormatType.list;
    setSearchResultsFormat(newFormat);
  }, [setSearchResultsFormat]);

  const rightSideContent = (
    <div className="flex flex-row items-center gap-2">
      {buttonsContent && (
        <>
          {buttonsContent}
          <VerticalDivider className="h-full" />
        </>
      )}
      <Tabs value={activePageTab} onValueChange={handlePageTabChange}>
        <TabsList>
          <TabsTrigger value={EnumPageTabs.Map}>Map</TabsTrigger>
          <TabsTrigger value={EnumPageTabs.List}>List</TabsTrigger>
        </TabsList>
      </Tabs>
      {actionsMenuItems.length > 0 && (
        <>
          <VerticalDivider className="h-full" />
          <ActionsMenu menuItems={actionsMenuItems} triggerPulse={isBatchSelectionActive} />
        </>
      )}
      {onSendCardClick && (
        <InkedButton onClick={onSendCardClick}>
          Handwritten Cards {sendCardCount > 0 && `(${sendCardCount})`}
        </InkedButton>
      )}
    </div>
  );

  return (
    <PageTitle
      title={title}
      titleIcon={titleIcon}
      superscriptText={isPro ? "Pro" : superscriptText}
      adjacentContent={
        <div className="hidden md:block">
          <SearchSummary
            totalRecords={totalRecords}
            pageSize={pageSize}
            currentPage={currentPage}
            isShowing={!isLoading && totalRecords}
          />
        </div>
      }
      centerContent={centerContent}
      subtitle={subtitle}
      isCondensed={isCondensed}
      isUseGutters={isUseGutters}
      rightSideContent={rightSideContent}
    />
  );
};

export default SearchPageTitle;